import { UseFormReturn } from 'react-hook-form';

import _ from 'lodash';

import { ProductTypeEnum } from '@schemas/scenarios-simulator/types';
import { ENERGY_DISTRIBUTION_COMPANIES } from './constants';
import {
  CostCompositionData,
  LegacyCostCompositionData,
} from '@components/organisms/scenarios-simulator/detailed-analysis/scenarios-projections/types';

export const edcList = Object.keys(ENERGY_DISTRIBUTION_COMPANIES).map((key) => ({
  optionLabel: ENERGY_DISTRIBUTION_COMPANIES[key as unknown as keyof typeof ENERGY_DISTRIBUTION_COMPANIES],
  value: Number(key),
}));

export const scrollToFirstError = <T extends Record<string, any>>(formController: UseFormReturn<T>) => {
  const { errors } = formController.formState;

  const hasErrors = Object.keys(errors)[0]?.length > 0;
  if (hasErrors) {
    const firstError = document.querySelector('.text-paragraph-small.text-orange-30');

    if (firstError) {
      firstError.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }
};

export const isObjectEmpty = (object?: Record<any, any> | null) => {
  if (object === undefined || object === null) return true;

  const objectValuesArray = Object.values(object);
  if (objectValuesArray?.length === 0) return true;
  else if (objectValuesArray.every((value) => !(value >= 0))) return true;
  else return false;
};

const transformEntityRecursively = (
  entity: Record<string, any> | Array<any>,
  transformKey: (key: string) => string,
) => {
  const restartRecursionForChildEntity = (childEntity: any) => {
    if (_.isPlainObject(childEntity) || _.isArray(childEntity)) transformEntityRecursively(childEntity, transformKey);
  };

  const transformList = (entityAsList: Array<any>) => {
    entityAsList.forEach((childEntity) => restartRecursionForChildEntity(childEntity));
  };

  const transformObject = (entityAsObject: Record<string, any>) => {
    Object.keys(entityAsObject).forEach((key) => {
      const transformedKey = transformKey(key);

      entityAsObject[transformedKey] = entityAsObject[key];
      if (key !== transformedKey) delete entityAsObject[key];

      restartRecursionForChildEntity(entityAsObject[transformedKey]);
    });
  };

  if (_.isArray(entity)) transformList(entity);
  else if (_.isPlainObject(entity)) transformObject(entity);

  return entity;
};

const customSnakeCase = (key: string) => {
  return key.split('__').map(_.snakeCase).join('__');
};

export const transformObjectKeysToSnakeCase = (object: Record<string, any>) => {
  const objectClone = _.cloneDeep(object);
  return transformEntityRecursively(objectClone, customSnakeCase);
};

const customCamelCase = (key: string) => {
  return key.split('__').map(_.camelCase).join('__');
};

export const transformObjectKeysToCamelCase = (object: Record<string, any>) => {
  const objectClone = _.cloneDeep(object);
  return transformEntityRecursively(objectClone, customCamelCase);
};

export const parseStringToBoolean = (string: string) => {
  if (string === 'true') return true;
  else if (string === 'false') return false;
  else return undefined;
};

export const isStringEmpty = (value?: string) => {
  if (!value || value === '') return true;
  else return false;
};
export const getAccumulatedDemand = (units?: Array<any>): number => {
  return (
    units?.reduce((accumulated, unit) => {
      const unitDemandOffPeakSum = unit.aclContractedDemandOffPeak ?? 0;
      return accumulated + unitDemandOffPeakSum;
    }, 0) ?? 0
  );
};

export const getProductType = (
  units?: Array<any>,
  productType?: keyof typeof ProductTypeEnum,
): keyof typeof ProductTypeEnum => {
  const accumulatedDemand = getAccumulatedDemand(units);

  if (accumulatedDemand < 500 && productType === 'ACL_WHOLESALER') {
    alert(
      'A modalidade de contratação foi automaticamente alterada para Varejista, pois esse grupo não possui demanda suficiente (>= 500 kW).',
    );
    return 'ACL_RETAILER';
  } else return productType as keyof typeof ProductTypeEnum;
};

export function filterServicesByUse(formValues?: any) {
  return (
    formValues?.additionalServices?.filter((service: any) => {
      return (
        service &&
        (service?.serviceName === 'CHANGE_OF_OWNERSHIP' ||
          service?.serviceName === 'DEMAND_ADJUSTMENT' ||
          service?.serviceName === 'TARIFF_MODALITY_CHANGE') &&
        service?.price !== null &&
        service?.price !== undefined
      );
    }) ?? []
  );
}

export const cleanedPayload = (formValues: any) => {
  const { use__additionalServices: _, ...formInputs } = formValues;
  const filteredAdditionalServices = filterServicesByUse(formValues);

  if (formValues.additionalServices?.length === 0) {
    formValues.additionalServices = null;
  }
  if (filteredAdditionalServices?.length > 0) {
    formValues.additionalServices = filteredAdditionalServices;
  }
  formInputs.attachments?.some((attachment: any) => {
    if (!attachment?.file_buffer && !attachment?.file_buffer) {
      formInputs.attachments = null;
    }
  });

  return { ...formInputs, additionalServices: filteredAdditionalServices };
};

export const isLowVoltageTariffGroup = (tariffGroup: string | number) => {
  const lowVoltageGroups = ['B3_STANDARD', 'B3_OPTING', 8, 9];
  return lowVoltageGroups.includes(tariffGroup);
};

export function isLegacyCostComposition(data: CostCompositionData): data is LegacyCostCompositionData {
  return !('distribution' in data);
}

export const getCalculatorName = (url: string): string => {
  if (url.includes('simulador-de-cenarios')) return 'Simulador de Cenários';
  if (url.includes('clarke-gestao')) return 'Clarke Gestão';
  if (url.includes('clarke-varejo')) return 'Clarke Varejo';
  if (url.includes('demanda-contratada')) return 'Demanda Contratada';
  return 'Calculadora';
};
